import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Modal, Input, FormGroup, Label, Button } from "reactstrap";
import { Form, Field } from "react-final-form";
import { getEvents } from "@/services/program/getEvents";
import { getEvent } from "@/services/program/getEvent";
import EVENT_TYPES from "@/shared/json/eventTypes.json";
import { labelizeNamedData, flashDispatch, flashMessage } from "@/shared/helpers";
import formValidation from "@/validation/givePeer2PeerReward";
import { connect } from "react-redux";
import axios from "axios";
import { getSocialWallPostTypeEvent } from "@/services/program/getSocialWallPostTypes";
import { createSocialWallPost } from "@/redux/actions/socialWallPostActions";
import ApiErrorMessage from "@/shared/components/flash/ApiErrorMessage";
import { useTranslation } from "react-i18next";
import { formatPoints } from "@/containers/Participant/Points/components/formatPoints";

const BASE_PRESET_POINTS = [500, 1000, 2000, 5000];

const RewardPeerPopup = ({
  isOpen,
  toggle,
  participants,
  program,
  organization,
  pointBalance,
  auth,
}) => {
  const { t } = useTranslation();
  const dispatch = flashDispatch();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [event, setEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedAmountMode, setSelectedAmountMode] = useState("preset");
  const [selectedPresetPoints, setSelectedPresetPoints] = useState(0);
  const [customPointsInput, setCustomPointsInput] = useState("");

  const p2p = EVENT_TYPES.find((type) => type.name === "peer2peer")?.value;
  const p2pBadge = EVENT_TYPES.find((type) => type.name === "peer2peer badge")?.value;
  const peerBalance = Number(pointBalance?.peerBalance ?? 0);
  const factorValue = Number(program?.factor_valuation ?? 1);
  const factor = factorValue > 0 ? factorValue : 1;
  const participantCount = Math.max(participants?.length ?? 0, 1);
  const primaryParticipant = participants?.[0] ?? null;
  const primaryParticipantName = primaryParticipant?.name || t("selected_participants");
  const additionalParticipants = Math.max((participants?.length ?? 0) - 1, 0);
  const participantInitials =
    primaryParticipantName
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") || "P";

  const maxAwardablePoints = useMemo(() => {
    let nextValue = Math.floor((peerBalance * factor) / participantCount);

    if (event) {
      const maxAwardableAmount = Number(event.max_awardable_amount ?? 0);
      if (maxAwardableAmount > 0 && maxAwardableAmount * participantCount < peerBalance) {
        nextValue = Math.floor(maxAwardableAmount * factor);
      }
    }

    return Math.max(nextValue, 0);
  }, [event, factor, participantCount, peerBalance]);

  const presetPointOptions = useMemo(() => {
    if (maxAwardablePoints <= 0) return [];

    const options = BASE_PRESET_POINTS.filter((value) => value <= maxAwardablePoints);

    if (options.length === 0 || maxAwardablePoints > options[options.length - 1]) {
      options.push(maxAwardablePoints);
    }

    return Array.from(new Set(options)).sort((a, b) => a - b);
  }, [maxAwardablePoints]);

  useEffect(() => {
    if (event?.event_type?.type === p2p) {
      const defaultPreset =
        presetPointOptions[presetPointOptions.length - 1] ?? maxAwardablePoints;

      setSelectedAmountMode("preset");
      setSelectedPresetPoints(defaultPreset > 0 ? defaultPreset : 0);
      setCustomPointsInput("");
      return;
    }

    setSelectedAmountMode("preset");
    setSelectedPresetPoints(0);
    setCustomPointsInput("");
  }, [event?.event_type?.type, event?.id, isOpen, maxAwardablePoints, p2p, presetPointOptions]);

  useEffect(() => {
    if (!organization?.id || !program?.id) {
      return;
    }

    let mounted = true;
    setLoading(true);

    let types = [p2p, p2pBadge];
    if (!peerBalance) {
      types = [p2pBadge];
    }

    getEvents(organization.id, program.id, { type: types })
      .then((items) => {
        if (!mounted) return;

        const nextEvents = items || [];
        const firstEvent = nextEvents[0] || null;
        setEvents(labelizeNamedData(nextEvents));
        setEvent(firstEvent);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        if (!mounted) return;
        setEvents([]);
        setEvent(null);
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [organization?.id, p2p, p2pBadge, peerBalance, program?.id]);

  const onSubmit = (values) => {
    if (event?.event_type?.type === p2p && (!hasValidPointsAmount || amountError)) {
      return;
    }

    const formData = {
      event_id: values.event_id,
      message: values.message,
      user_id: participants.map((participant) => participant.id),
      override_cash_value:
        event?.event_type?.type === p2p ? pointsToSend / factor : null,
    };

    setSaving(true);
    axios
      .post(`/organization/${organization.id}/program/${program.id}/award/`, formData)
      .then((res) => {
        if (res.status === 200) {
          dispatch(flashMessage(t("award_success"), "alert-success", "top"));

          if (program.uses_social_wall) {
            const resultObject = res.data;

            getSocialWallPostTypeEvent(organization.id, program.id).then(
              (socialWallPostTypeEvent) => {
                Object.keys(resultObject).forEach((key) => {
                  const resultData = resultObject[key];
                  const socialWallPostData = {
                    social_wall_post_type_id: socialWallPostTypeEvent.id,
                    social_wall_post_id: null,
                    event_xml_data_id: resultData.event_xml_data_id,
                    program_id: program.id,
                    organization_id: organization.id,
                    awarder_program_id: null,
                    sender_user_account_holder_id: auth?.account_holder_id,
                    receiver_user_account_holder_id: resultData.userAccountHolderId,
                  };

                  dispatch(
                    createSocialWallPost(
                      organization.id,
                      program.id,
                      socialWallPostData
                    )
                  );
                });
              }
            );
          }

          window.location.reload();
        }
      })
      .catch((err) => {
        dispatch(
          flashMessage(
            <ApiErrorMessage errors={err.response?.data} />,
            "alert-danger",
            "top"
          )
        );
        setSaving(false);
      });
  };

  const handlePresetSelect = (points) => {
    setSelectedAmountMode("preset");
    setSelectedPresetPoints(points);
  };

  const handleCustomSelect = () => {
    setSelectedAmountMode("custom");
    setCustomPointsInput((current) =>
      current || (selectedPresetPoints > 0 ? String(selectedPresetPoints) : "")
    );
  };

  const onChangeEvent = async (selectedOption, form) => {
    if (!selectedOption) return;

    const item = await getEvent(organization.id, program.id, selectedOption.value);
    setEvent(item);
    form.change("event_id", item.id);
    form.change("message", item.message || "");
  };

  if (loading) return t("loading");

  const isPointsReward = event?.event_type?.type === p2p;
  const parsedCustomPoints =
    customPointsInput.trim() && /^\d+$/.test(customPointsInput.trim())
      ? Number(customPointsInput.trim())
      : 0;
  const rawPointsToSend = isPointsReward
    ? selectedAmountMode === "custom"
      ? parsedCustomPoints
      : selectedPresetPoints
    : maxAwardablePoints;
  const amountError = isPointsReward
    ? selectedAmountMode === "custom"
      ? !customPointsInput.trim()
        ? t("select_reward_amount")
        : parsedCustomPoints <= 0
          ? t("enter_valid_points_amount")
          : parsedCustomPoints > maxAwardablePoints
            ? t("reward_amount_max_points", {
                points: formatPoints(maxAwardablePoints),
              })
            : ""
      : selectedPresetPoints <= 0
        ? t("select_reward_amount")
        : ""
    : "";
  const hasValidPointsAmount = !amountError && rawPointsToSend > 0;
  const pointsToSend = isPointsReward
    ? hasValidPointsAmount
      ? rawPointsToSend
      : 0
    : maxAwardablePoints;
  const recipientSummary =
    additionalParticipants > 0
      ? t("participant_and_others", {
          name: primaryParticipantName,
          count: additionalParticipants,
        })
      : primaryParticipantName;
  const sendingStatement = isPointsReward
    ? hasValidPointsAmount
      ? t("you_are_sending_points", { points: formatPoints(pointsToSend) })
      : t("select_reward_amount")
    : event?.name || t("send_reward");
  const confirmationStatement =
    isPointsReward
      ? hasValidPointsAmount
        ? additionalParticipants > 0
          ? t("this_will_send_points_each_to", {
              points: formatPoints(pointsToSend),
              name: recipientSummary,
            })
          : t("this_will_send_points_to", {
              points: formatPoints(pointsToSend),
              name: recipientSummary,
            })
        : t("select_reward_amount")
      : t("this_will_send_reward_to", { name: recipientSummary });
  const ctaLabel =
    isPointsReward && hasValidPointsAmount
      ? t("send_points_cta", { points: formatPoints(pointsToSend) })
      : t("send_reward");
  const displayedAmount = rawPointsToSend > 0
    ? `${formatPoints(rawPointsToSend)} ${t("points")}`
    : "—";
  const isSubmitDisabled = saving || (isPointsReward && !hasValidPointsAmount);

  let eventIconSrc = `${process.env.PUBLIC_URL}/img/award-event-icon.png`;
  if (event?.icon?.id) {
    eventIconSrc = `${process.env.REACT_APP_API_STORAGE_URL}/${event.icon.path}`;
  }
  const selectedEventOption =
    events.find((item) => String(item.value) === String(event?.id)) || null;

  const initialValues = {
    event_id: event?.id,
    awarding_points: maxAwardablePoints,
    message: event?.message || "",
    email_template_id: 1,
  };

  return (
    <Modal className="peer-reward-modal modal-lg" isOpen={isOpen} toggle={toggle}>
      <div className="peer-reward-modal__card">
        <div className="peer-reward-modal__header">
          <div>
            <h3 className="peer-reward-modal__title">{t("reward_a_peer")}</h3>
            <p className="peer-reward-modal__subtitle">
              {participants.length} {t("selected_participants")}
            </p>
          </div>
          <button
            type="button"
            className="peer-reward-modal__close"
            onClick={toggle}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="peer-reward-modal__body">
          <div className="peer-reward-modal__summary">
            <div className="peer-reward-modal__field">
              <span className="peer-reward-modal__field-label">{t("rewarding")}</span>
              <div className="peer-reward-modal__recipient">
                <div className="peer-reward-modal__recipient-avatar" aria-hidden="true">
                  {participantInitials}
                </div>
                <div className="peer-reward-modal__recipient-body">
                  <div className="peer-reward-modal__recipient-name-row">
                    <strong className="peer-reward-modal__recipient-name">
                      {primaryParticipantName}
                    </strong>
                    {additionalParticipants > 0 && (
                      <span className="peer-reward-modal__recipient-extra">
                        {t("plus_others", { count: additionalParticipants })}
                      </span>
                    )}
                  </div>
                  <p className="peer-reward-modal__summary-points">{sendingStatement}</p>
                </div>
              </div>
            </div>
          </div>

          <Form
            onSubmit={onSubmit}
            initialValues={initialValues}
            validate={(values) => formValidation.validateForm(values)}
          >
            {({ handleSubmit, form }) => (
              <form className="peer-reward-modal__form" onSubmit={handleSubmit}>
                <div className="peer-reward-modal__field-grid">
                  <div className="peer-reward-modal__field">
                    <Label className="peer-reward-modal__label">{t("select_an_event")}</Label>
                    <div className="peer-reward-modal__event-picker">
                      <div className="peer-reward-modal__event-icon">
                        <img alt="Event" src={eventIconSrc} />
                      </div>
                      <Field name="event_id">
                        {({ input, meta }) => (
                          <FormGroup className="peer-reward-modal__control">
                            <Select
                              onChange={(selectedOption) => {
                                input.onChange(selectedOption?.value);
                                onChangeEvent(selectedOption, form);
                              }}
                              options={events}
                              isClearable={false}
                              className="react-select"
                              placeholder={t("select_an_event")}
                              classNamePrefix="react-select"
                              value={selectedEventOption}
                            />
                            {meta.touched && meta.error && (
                              <span className="text-danger">{meta.error}</span>
                            )}
                          </FormGroup>
                        )}
                      </Field>
                    </div>
                  </div>

                  {event?.event_type?.type === p2p && (
                    <div className="peer-reward-modal__field">
                      <div className="peer-reward-modal__amount-top">
                        <Label className="peer-reward-modal__label">{t("reward_amount")}</Label>
                        <strong className="peer-reward-modal__amount-value">{displayedAmount}</strong>
                      </div>
                      <div className="peer-reward-modal__amount-options">
                        {presetPointOptions.map((points) => (
                          <button
                            key={points}
                            type="button"
                            className={`peer-reward-modal__amount-option ${
                              selectedAmountMode === "preset" &&
                              selectedPresetPoints === points
                                ? "is-active"
                                : ""
                            }`}
                            onClick={() => handlePresetSelect(points)}
                          >
                            {formatPoints(points)} {t("points")}
                          </button>
                        ))}
                        <button
                          type="button"
                          className={`peer-reward-modal__amount-option ${
                            selectedAmountMode === "custom" ? "is-active" : ""
                          }`}
                          onClick={handleCustomSelect}
                        >
                          {t("custom_amount")}
                        </button>
                      </div>
                      {selectedAmountMode === "custom" && (
                        <FormGroup className="peer-reward-modal__control peer-reward-modal__amount-custom">
                          <Input
                            className="peer-reward-modal__custom-input"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            placeholder={t("enter_points")}
                            type="text"
                            value={customPointsInput}
                            onChange={(inputEvent) => {
                              setCustomPointsInput(
                                inputEvent.target.value.replace(/[^\d]/g, "")
                              );
                            }}
                          />
                        </FormGroup>
                      )}
                      {amountError && (
                        <div className="peer-reward-modal__error">{amountError}</div>
                      )}
                    </div>
                  )}

                  <div className="peer-reward-modal__field peer-reward-modal__field--full">
                    <Label className="peer-reward-modal__label">{t("message_to_participant")}</Label>
                    <Field name="message">
                      {({ input, meta }) => (
                        <FormGroup className="peer-reward-modal__control">
                          <Input
                            className="peer-reward-modal__message"
                            placeholder={t("message_to_participant")}
                            type="textarea"
                            rows="4"
                            {...input}
                          />
                          {meta.touched && meta.error && (
                            <span className="text-danger">{meta.error}</span>
                          )}
                        </FormGroup>
                      )}
                    </Field>
                  </div>
                </div>

                <div className="peer-reward-modal__actions">
                  <p className="peer-reward-modal__confirmation">{confirmationStatement}</p>
                  <Button
                    className="peer-reward-modal__submit"
                    type="submit"
                    disabled={isSubmitDisabled}
                  >
                    {ctaLabel}
                  </Button>
                </div>
              </form>
            )}
          </Form>
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  pointBalance: state.pointBalance,
  auth: state.auth,
});

export default connect(mapStateToProps)(RewardPeerPopup);
